import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  FormGroup, FormControlLabel, Checkbox, Button, Snackbar, Alert, CircularProgress
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import api from '../utils/api';

interface Role { id: number; code: string; name: string; description: string; }
interface Permission { id: number; code: string; name: string; description: string; category: string; }

const RolesTab: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [rolePerms, setRolePerms] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/roles'),
        api.get('/roles/permissions'),
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);

      // Загружаем права для каждой роли
      const permsMap: Record<string, string[]> = {};
      for (const role of rolesRes.data) {
        const res = await api.get(`/roles/${role.code}/permissions`);
        permsMap[role.code] = res.data;
      }
      setRolePerms(permsMap);
    } catch (err) {
      console.error('Ошибка загрузки ролей:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (roleCode: string, permCode: string) => {
    setRolePerms(prev => {
      const current = prev[roleCode] || [];
      const updated = current.includes(permCode)
        ? current.filter(c => c !== permCode)
        : [...current, permCode];
      return { ...prev, [roleCode]: updated };
    });
  };

  const handleSave = async (roleCode: string) => {
    setSaving(roleCode);
    try {
      await api.post(`/roles/${roleCode}/permissions`, {
        permissionCodes: rolePerms[roleCode] || []
      });
      setSnackbar({ open: true, message: `Права для роли "${roleCode}" сохранены`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Ошибка сохранения', severity: 'error' });
    } finally {
      setSaving(null);
    }
  };

  // Группируем права по категориям
  const groupedPermissions: Record<string, Permission[]> = {};
  for (const p of permissions) {
    if (!groupedPermissions[p.category]) groupedPermissions[p.category] = [];
    groupedPermissions[p.category].push(p);
  }

  const categoryNames: Record<string, string> = {
    general: 'Общее',
    guides: 'Экскурсоводы',
    tours: 'Программы',
    extra: 'Доп. программы',
    calendar: 'Календарь',
    upload: 'Загрузка',
    summary: 'Расчеты',
    orders: 'Заказы',
    distribution: 'Распределение',
    availability: 'Доступность',
    aliases: 'Алиасы',
    statistics: 'Статистика',
    periods: 'Периоды',
    settings: 'Настройки',
    users: 'Пользователи',
    roles: 'Роли',
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Роли и права доступа</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Отметьте права для каждой роли. Администратор имеет полный доступ, Менеджер — ко всем разделам, кроме управления пользователями и ролями.
      </Typography>

      {roles.map((role) => (
        <Accordion key={role.code} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{role.name}</Typography>
              <Typography variant="caption" color="textSecondary">{role.description}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {Object.entries(groupedPermissions).map(([category, perms]) => (
              <Box key={category} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                  {categoryNames[category] || category}
                </Typography>
                <FormGroup row>
                  {perms.map((p) => (
                    <FormControlLabel
                      key={p.code}
                      control={
                        <Checkbox
                          size="small"
                          checked={(rolePerms[role.code] || []).includes(p.code)}
                          onChange={() => handleToggle(role.code, p.code)}
                          disabled={role.code === 'admin'}
                        />
                      }
                      label={<Typography variant="body2">{p.name}</Typography>}
                      sx={{ minWidth: 250 }}
                    />
                  ))}
                </FormGroup>
              </Box>
            ))}
            <Button
              variant="contained"
              size="small"
              onClick={() => handleSave(role.code)}
              disabled={saving === role.code || role.code === 'admin'}
              sx={{ mt: 1 }}
            >
              {saving === role.code ? 'Сохранение...' : 'Сохранить права'}
            </Button>
          </AccordionDetails>
        </Accordion>
      ))}

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default RolesTab;
